import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import PageShell from "@/components/site/PageShell";
import { localizeCategory } from "@/i18n/categories";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Convert **text** Markdown bold and [text](url) Markdown links
function renderParagraph(text) {
  const parts = text.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      return (
        <strong key={i} className="text-[#1a365d]">
          {p.slice(2, -2)}
        </strong>
      );
    }
    const linkMatch = p.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (linkMatch) {
      const [, label, href] = linkMatch;
      const isExternal = /^https?:\/\//.test(href);
      return (
        <a
          key={i}
          href={href}
          {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
          className="text-[#1a365d] font-medium underline decoration-[#d4af37] decoration-2 underline-offset-[6px] hover:text-[#d4af37] transition-colors"
        >
          {label}
        </a>
      );
    }
    return <span key={i}>{p}</span>;
  });
}

export default function ArticleDetail() {
  const { slug } = useParams();
  const { i18n, t } = useTranslation();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const { data } = await axios.get(`${API}/articles/${slug}`, { params: { lang: i18n.language } });
        if (!cancelled) setArticle(data);
      } catch (e) {
        if (!cancelled) setError("Articolo non trovato.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, i18n.language]);

  if (loading) {
    return (
      <PageShell>
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-32 text-center text-[#475569]">
          Caricamento articolo…
        </div>
      </PageShell>
    );
  }

  if (error || !article) {
    return (
      <PageShell>
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-32 text-center">
          <h1 className="font-display text-4xl text-[#1a365d]">404</h1>
          <p className="mt-4 text-[#475569]">{error || t("common.articleNotFound")}</p>
          <Link to="/news-archive" className="btn-outline mt-8">
            <ArrowLeft size={14} /> {t("common.otherArticles")}
          </Link>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      {/* Hero */}
      <section className="bg-[#fafaf7] border-b border-[#e2e8f0]">
        <div className="max-w-4xl mx-auto px-6 md:px-10 pt-12 md:pt-16 pb-10 md:pb-14">
          <Link
            to="/news-archive"
            className="inline-flex items-center gap-2 text-[10.5px] tracking-[0.28em] uppercase text-[#475569] hover:text-[#1a365d]"
          >
            <ArrowLeft size={12} /> News Archive
          </Link>
          <div className="mt-8 flex items-center gap-4 flex-wrap">
            <span className="bg-[#1a365d] text-white px-3 py-1 text-[10px] tracking-[0.24em] uppercase font-medium">
              {localizeCategory(article.category, i18n.language)}
            </span>
            <span className="text-[11px] tracking-[0.28em] uppercase text-[#475569]">
              {article.date}
            </span>
          </div>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#1a365d] mt-6 leading-[1.05] tracking-tight">
            {article.title}
          </h1>
          {article.subtitle && (
            <p className="mt-5 font-display italic text-xl md:text-2xl text-[#475569] leading-snug">
              {article.subtitle}
            </p>
          )}
        </div>
      </section>

      {/* Hero image */}
      <section className="bg-[#fafaf7]">
        <div className="max-w-5xl mx-auto px-6 md:px-10 pb-16">
          <div className="aspect-[16/9] w-full overflow-hidden bg-[#0f2340]">
            <img
              src={article.hero_image}
              alt={article.title}
              className={`w-full h-full ${article.hero_fit === "contain" ? "object-contain" : "object-cover"}`}
            />
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <div className="space-y-7 text-[#1e293b] leading-[1.9] text-base md:text-[17px]">
            {article.body.map((p, i) => {
              // Inline image or image pair block
              if (typeof p === "object" && p !== null) {
                if (p.type === "image_pair") {
                  return (
                    <div key={i} className="my-10 grid grid-cols-1 md:grid-cols-2 gap-4 reveal">
                      {p.images.map((img, j) => (
                        <div key={j} className="aspect-[4/3] w-full overflow-hidden bg-[#fafaf7]">
                          <img
                            src={img}
                            alt={p.caption || article.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ))}
                      {p.caption && (
                        <p className="col-span-full text-[12px] italic text-[#64748b] text-center mt-1">
                          {p.caption}
                        </p>
                      )}
                    </div>
                  );
                }
                if (p.type === "image") {
                  return (
                    <div key={i} className="my-10 reveal">
                      <div className="w-full overflow-hidden bg-[#fafaf7] border border-[#e2e8f0]">
                        <img
                          src={p.src}
                          alt={p.caption || article.title}
                          className="w-full h-auto block"
                        />
                      </div>
                      {p.caption && (
                        <p className="text-[12px] italic text-[#64748b] text-center mt-3">
                          {p.caption}
                        </p>
                      )}
                    </div>
                  );
                }
              }
              return (
                <p key={i} className="reveal">
                  {renderParagraph(p)}
                </p>
              );
            })}
            {article.secondary_image && (
              <div className="my-10 aspect-[16/9] w-full overflow-hidden">
                <img
                  src={article.secondary_image}
                  alt={article.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
          </div>

          <div className="mt-16 pt-10 border-t border-[#e2e8f0] flex flex-wrap items-center gap-4">
            <Link to="/news-archive" className="btn-outline">
              <ArrowLeft size={14} /> {t("common.otherArticles")}
            </Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
